import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useTenantStore } from '@/hooks/useTenantStore';
import { toast } from 'sonner';
import { Loader2, Sparkles, TrendingUp, Users, AlertCircle, RefreshCw } from 'lucide-react';

interface Match {
  listing_id: string;
  buyer_id: string;
  score: number;
  listing_details: {
    shares: number;
    price_per_share: number;
    share_class: string;
    seller_name: string;
  };
  buyer_details: {
    name: string;
    email: string;
  };
  reasons: string[];
}

interface SmartMatchingPanelProps {
  companyId?: string;
}

export default function SmartMatchingPanel({ companyId: propCompanyId }: SmartMatchingPanelProps) {
  const { user } = useAuth();
  const { tenantId } = useTenantStore();
  const companyId = propCompanyId || tenantId;
  const { isOwner, isAdmin, isInvestor } = useUserRoles(companyId);
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const hasAccess = isOwner(companyId) || isAdmin(companyId) || isInvestor(companyId);

  useEffect(() => {
    if (companyId && hasAccess) {
      loadMatches();
    }
  }, [companyId, hasAccess]);

  async function loadMatches() {
    if (!companyId) return;

    setLoading(true);
    try {
      // Load matches with manual joins
      const { data: matchesData, error: matchesError } = (await supabase
        .from('smart_matches' as any)
        .select('*')
        .eq('company_id', companyId)
        .order('score', { ascending: false })) as any;

      if (matchesError) throw matchesError;

      if (!matchesData || matchesData.length === 0) {
        setMatches([]);
        return;
      }

      // Get listing details
      const listingIds = matchesData.map((m: any) => m.listing_id);
      const { data: listingsData } = await supabase
        .from('secondary_listings')
        .select('id, shares, price_per_share, share_class, seller_id')
        .in('id', listingIds);

      // Get seller and buyer details
      const sellerIds = listingsData?.map((l: any) => l.seller_id) || [];
      const buyerIds = matchesData.map((m: any) => m.buyer_id);
      const allUserIds = [...new Set([...sellerIds, ...buyerIds])];

      const { data: identitiesData } = await supabase
        .from('investor_identities')
        .select('user_id, shareholder_id')
        .in('user_id', allUserIds);

      const shareholderIds = identitiesData?.map((i: any) => i.shareholder_id) || [];
      const { data: shareholdersData } = await supabase
        .from('shareholders')
        .select('id, name, email')
        .in('id', shareholderIds);

      // Build lookup maps
      const listingsMap = new Map(listingsData?.map((l: any) => [l.id, l]) || []);
      const identitiesMap = new Map(identitiesData?.map((i: any) => [i.user_id, i]) || []);
      const shareholdersMap = new Map(shareholdersData?.map((s: any) => [s.id, s]) || []);

      // Transform matches
      const transformedMatches: Match[] = matchesData.map((m: any) => {
        const listing = listingsMap.get(m.listing_id);
        const sellerIdentity = listing ? identitiesMap.get(listing.seller_id) : null;
        const sellerShareholder = sellerIdentity
          ? shareholdersMap.get(sellerIdentity.shareholder_id)
          : null;

        const buyerIdentity = identitiesMap.get(m.buyer_id);
        const buyerShareholder = buyerIdentity
          ? shareholdersMap.get(buyerIdentity.shareholder_id)
          : null;

        return {
          listing_id: m.listing_id,
          buyer_id: m.buyer_id,
          score: m.score,
          listing_details: {
            shares: listing?.shares || 0,
            price_per_share: listing?.price_per_share || 0,
            share_class: listing?.share_class || '',
            seller_name: sellerShareholder?.name || 'Unknown',
          },
          buyer_details: {
            name: buyerShareholder?.name || 'Unknown',
            email: buyerShareholder?.email || '',
          },
          reasons: m.reasons || [],
        };
      });

      setMatches(transformedMatches);
    } catch (error: any) {
      console.error('Error loading matches:', error);
      toast.error('Failed to load matches');
    } finally {
      setLoading(false);
    }
  }

  async function generateMatches() {
    if (!companyId) return;

    setGenerating(true);
    toast('Generating smart matches...');

    try {
      const { error } = await supabase.functions.invoke('generate-smart-matches', {
        body: { companyId },
      });

      if (error) throw error;

      toast.success('Smart matches generated successfully');
      loadMatches();
    } catch (error: any) {
      console.error('Error generating matches:', error);
      toast.error(error.message || 'Failed to generate matches');
    } finally {
      setGenerating(false);
    }
  }

  if (!hasAccess) {
    return (
      <DashboardLayout>
        <Card>
          <CardContent className="flex items-center justify-center py-8">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">You don't have access to this feature</p>
            </div>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  if (!companyId) {
    return (
      <DashboardLayout>
        <Card>
          <CardContent className="flex items-center justify-center py-8">
            <p className="text-muted-foreground">Please select a company</p>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Sparkles className="h-8 w-8" />
              Smart Matching
            </h1>
            <p className="text-muted-foreground mt-1">
              AI-powered buyer-seller matching for optimal liquidity
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={loadMatches} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={generateMatches} disabled={generating || loading}>
              {generating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4 mr-2" />
              )}
              Generate Matches
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid sm:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Matches</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{matches.length}</div>
              <p className="text-xs text-muted-foreground">Active potential matches</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">High Score Matches</CardTitle>
              <Sparkles className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {matches.filter((m) => m.score >= 80).length}
              </div>
              <p className="text-xs text-muted-foreground">Score ≥ 80%</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Unique Buyers</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {new Set(matches.map((m) => m.buyer_id)).size}
              </div>
              <p className="text-xs text-muted-foreground">Interested investors</p>
            </CardContent>
          </Card>
        </div>

        {/* Matches Table */}
        <Card>
          <CardHeader>
            <CardTitle>Recommended Matches</CardTitle>
            <CardDescription>Sorted by match score - highest potential first</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : matches.length === 0 ? (
              <div className="text-center py-8 border border-dashed rounded-lg">
                <Sparkles className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-sm text-muted-foreground">No matches found</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Generate matches to see AI-powered recommendations
                </p>
              </div>
            ) : (
              <div className="rounded-lg border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Match Score</TableHead>
                      <TableHead>Seller</TableHead>
                      <TableHead>Share Class</TableHead>
                      <TableHead className="text-right">Shares</TableHead>
                      <TableHead className="text-right">Price/Share</TableHead>
                      <TableHead>Buyer</TableHead>
                      <TableHead>Reasons</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {matches.map((match, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Badge
                            variant={
                              match.score >= 80
                                ? 'default'
                                : match.score >= 60
                                  ? 'secondary'
                                  : 'outline'
                            }
                          >
                            {match.score}%
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">
                          {match.listing_details.seller_name}
                        </TableCell>
                        <TableCell>{match.listing_details.share_class}</TableCell>
                        <TableCell className="text-right">
                          {match.listing_details.shares.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          R{match.listing_details.price_per_share.toFixed(4)}
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{match.buyer_details.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {match.buyer_details.email}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <ul className="text-xs space-y-1">
                            {match.reasons.map((reason, i) => (
                              <li key={i} className="text-muted-foreground">
                                • {reason}
                              </li>
                            ))}
                          </ul>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* How It Works */}
        <Card>
          <CardHeader>
            <CardTitle>How Smart Matching Works</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              <strong>AI-Powered Analysis:</strong> Our system analyzes buyer investment history,
              preferences, and behavior to predict match quality.
            </p>
            <p>
              <strong>Multi-Factor Scoring:</strong> Considers price alignment, share class
              preferences, transaction history, and timing.
            </p>
            <p>
              <strong>Dynamic Updates:</strong> Matches are re-scored as new data becomes available,
              ensuring optimal recommendations.
            </p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
